export type RemovalRunResolutionEntry = {
  sourceCycleId: number;
  targetCycleId: number;
  status: string;
  ok: boolean;
  failureReason?: string | null;
};

export function formatCyclePairLabel(
  entry: Pick<RemovalRunResolutionEntry, 'sourceCycleId' | 'targetCycleId'>,
  cycleNameById?: Map<number, string>,
): string {
  const source = cycleNameById?.get(entry.sourceCycleId) ?? `cycle ${entry.sourceCycleId}`;
  const target = cycleNameById?.get(entry.targetCycleId) ?? `cycle ${entry.targetCycleId}`;
  return `${source} → ${target}`;
}

export function buildRemovalResolutionUserMessage(
  entry: RemovalRunResolutionEntry,
  cycleNameById?: Map<number, string>,
): string {
  const pair = formatCyclePairLabel(entry, cycleNameById);
  switch (entry.status) {
    case 'no_active_run':
      return `No removal list exists yet for ${pair}. Posting tasks below are unaffected.`;
    case 'no_matching_mode':
      return `The removal list for ${pair} is out of date — new posting confirmations arrived after it was last calculated. It will refresh automatically when confirmations continue (or after ops repair). Posting tasks below are unaffected.`;
    case 'ambiguous_matching_mode':
      return `More than one active removal list matches ${pair}, so the system will not guess which is correct. Posting tasks below are unaffected.`;
    default:
      return `Removals are unavailable for ${pair} right now. Posting tasks below are unaffected.`;
  }
}

export function parseRemovalRunResolutionEntries(value: unknown): RemovalRunResolutionEntry[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is RemovalRunResolutionEntry => {
    if (!entry || typeof entry !== 'object') return false;
    const row = entry as Record<string, unknown>;
    return typeof row.sourceCycleId === 'number'
      && typeof row.targetCycleId === 'number'
      && typeof row.status === 'string'
      && typeof row.ok === 'boolean';
  });
}
